import type { SurfaceMaterial } from '../world/Surfaces';

// Fully synthesized audio (§9). Avoids binary assets and starts on a user
// gesture (§8). Discrete SFX only — no continuous ambient bed.

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  enabled = true;
  private started = false;

  /** Must be called from a user gesture (tap/click/key). */
  async start(): Promise<void> {
    if (this.started) {
      await this.ctx?.resume();
      return;
    }
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctx();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.enabled ? 0.8 : 0;
    this.master.connect(this.ctx.destination);
    this.started = true;
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    if (this.master) this.master.gain.value = on ? 0.8 : 0;
  }

  /** No-op: the continuous rolling/wind bed was removed. */
  updateRumble(_speed01: number, _grounded: boolean, _surface: SurfaceMaterial): void {
    /* intentionally silent */
  }

  private blip(freq: number, dur: number, type: OscillatorType = 'sine', gain = 0.3): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(g).connect(this.master);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  pickup(comboLevel: number, jackpot: boolean): void {
    // Rising pitch with the combo (§9).
    const base = 520 + Math.min(comboLevel, 16) * 45;
    this.blip(base, 0.12, 'triangle', 0.25);
    if (jackpot) {
      // Chunkier, jackpot-y payoff for coins / red envelopes.
      this.blip(base * 1.5, 0.18, 'square', 0.2);
      window.setTimeout(() => this.blip(base * 2, 0.22, 'triangle', 0.22), 70);
    }
  }

  jump(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(300, t);
    osc.frequency.exponentialRampToValueAtTime(720, t + 0.18);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.25, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.2);
    osc.connect(g).connect(this.master);
    osc.start(t);
    osc.stop(t + 0.22);
  }

  land(intensity: number): void {
    this.blip(120 + intensity * 60, 0.1, 'sine', 0.2 + intensity * 0.15);
  }

  bounce(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(220, t);
    osc.frequency.exponentialRampToValueAtTime(880, t + 0.25);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.3, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
    osc.connect(g).connect(this.master);
    osc.start(t);
    osc.stop(t + 0.32);
  }

  fall(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(440, t);
    osc.frequency.exponentialRampToValueAtTime(80, t + 0.5);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.25, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
    osc.connect(g).connect(this.master);
    osc.start(t);
    osc.stop(t + 0.52);
  }

  thud(): void {
    this.blip(90, 0.14, 'square', 0.18);
  }

  finish(): void {
    // Little ascending fanfare.
    const notes = [523, 659, 784, 1047];
    notes.forEach((f, i) => window.setTimeout(() => this.blip(f, 0.3, 'triangle', 0.25), i * 110));
  }
}
